// src/components/organisation/renderers/ContactSectionRenderer.jsx
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, ExternalLink } from 'lucide-react';

export const ContactSectionRenderer = ({ 
  data, 
  bookData, 
  customStyling = {}, 
  onInteraction 
}) => {
  const {
    heading = 'Get In Touch',
    subHeading = '',
    email = '',
    phone = '',
    address = '',
    businessHours = [],
    showForm = true,
    mapUrl = '',
    additionalContacts = [],
    socialLinks = [],
    enabled = true
  } = data || {};

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!enabled) return null;

  const trackInteraction = (action, details = {}) => {
    if (onInteraction) {
      onInteraction({
        type: 'contact_interaction',
        action,
        section: 'contact',
        sectionId: data?.id,
        ...details
      });
    }
  };

  const handleContactClick = (type, value) => {
    trackInteraction('contact_click', { type, value });
    
    if (type === 'email') {
      window.location.href = `mailto:${value}`;
    } else if (type === 'phone') {
      window.location.href = `tel:${value}`;
    } else if (type === 'map') {
      window.open(value, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackInteraction('form_submit', { formData });
    
    // Simulate form submission - replace with actual implementation
    setTimeout(() => {
      alert('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section className="contact-section py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        {(heading || subHeading) && (
          <div className="text-center mb-16">
            {heading && (
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {heading}
              </h2>
            )}
            {subHeading && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {subHeading}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact information */}
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
            
            <div className="space-y-6">
              {/* Primary contact info */}
              {email && (
                <div 
                  className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleContactClick('email', email)}
                >
                  <Mail className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <p className="text-gray-600">{email}</p>
                  </div>
                </div>
              )}

              {phone && (
                <div 
                  className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleContactClick('phone', phone)}
                >
                  <Phone className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Phone</p>
                    <p className="text-gray-600">{phone}</p>
                  </div>
                </div>
              )}

              {address && (
                <div 
                  className={`flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow ${mapUrl ? 'cursor-pointer' : ''}`}
                  onClick={mapUrl ? () => handleContactClick('map', mapUrl) : undefined}
                >
                  <MapPin className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Address</p>
                    <p className="text-gray-600">{address}</p>
                    {mapUrl && (
                      <div className="flex items-center text-blue-600 text-sm mt-1">
                        <span>View on map</span>
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Business hours */}
              {businessHours?.length > 0 && (
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm">
                  <Clock className="h-6 w-6 text-blue-600 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900 mb-2">Business Hours</p>
                    <div className="space-y-1">
                      {businessHours.map((hours, index) => (
                        <p key={index} className="text-gray-600 text-sm">
                          <span className="font-medium">{hours.day}:</span> {hours.hours}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional contacts */}
              {additionalContacts?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Additional Contacts</h4>
                  {additionalContacts.map((contact, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg shadow-sm">
                      <p className="font-medium text-gray-900 text-sm">{contact.label}</p>
                      <p className="text-gray-600 text-sm">{contact.value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact form */}
          {showForm && (
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h3>
              
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What is this regarding?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please provide details about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};